import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BridgeError, createRestoreBridge, type AgentEvent, type BridgeMessage, type DaemonState, type RestoreBridge } from './bridge'
import { restoreRunsApi, type RestoreRun } from './api'

/**
 * Driving the restore agent's self-test from the page, and keeping Orchestrate's record of it.
 *
 * The order of things (Andrew's task, 13 Oct 2026; the bridge document, section 4):
 *   1. On load, and whenever the plug-in's link to the daemon comes back, send restoreSync with
 *      the last sequence number STORED for each of this person's active runs. The daemon replays
 *      what was missed, so a run started before the page was left is caught up, not lost.
 *   2. To start: record the run in Orchestrate FIRST (so the database can refuse a busy target),
 *      then send restoreRun. If the daemon will not start it, the record is closed as refused.
 *   3. Every restoreEvent is saved through Orchestrate's own back end as the signed-in user, in
 *      sequence order, and the run row shown is the one the back end returns - the page shows
 *      what was stored, not what it thinks happened. A repeat is ignored by the back end.
 *   4. A gap in the sequence means something was missed: send restoreSync.
 *
 * The session token is the sign-in pass from the back end (useSessionPass); it is held in memory
 * only. When the daemon answers `unauthorized` the pass was refused, so a new one is asked for.
 */
export const COMMAND = 'self-test'
// Every run of this person's, self-tests and real restores alike: one list, one event pipeline.
const KEY = ['restore-runs', 'mine'] as const

export interface SelfTestOptions { [option: string]: string | number | boolean }
export type LinkState = 'unknown' | 'open' | 'closed'

const pad = (n: number, width = 2) => String(n).padStart(width, '0')
/** A new run id every time: the daemon refuses one it has already seen. */
export function newRunId(now = new Date(), random = Math.random(), prefix = 'selftest') {
    const stamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}-${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}`
    return `${prefix}-${stamp}-${Math.floor(random * 0xffff).toString(16).padStart(4, '0')}`
}

/** What a real restore needs from the page: the job's parts and, separately, the typed secret values. */
export interface RealRun {
    command: string
    operationClass: 'read' | 'restore' | 'destroy'
    targetRef: string
    options: Record<string, string | number | boolean>
    /** Exactly the secrets the job names, by name; the values travel only to the plug-in. */
    secrets: Record<string, string>
    /** restore-raw-image only (the Bridge document, section 3a): bytes a second, 0 or absent for no limit. */
    rateLimitBytesPerSecond?: number
    /** false: start without restorePlan first (the setup screen's signed self-test, which has no target to claim). */
    plan?: boolean
}
/** The ids `restorePlan` answered with, from its `accepted` event (the Bridge document, section 3). */
export function plannedIds(data: unknown): { targetId: string | null; objectId: string | null; refused: { code: string | null; message: string | null } | null } {
    const events = ((data as { events?: AgentEvent[] } | undefined)?.events ?? []) as AgentEvent[]
    const accepted = events.find((e) => e.type === 'accepted')
    const refused = events.find((e) => e.type === 'refused' || e.type === 'failed')
    const text = (v: unknown) => (typeof v === 'string' && v ? v : null)
    return {
        targetId: text(accepted?.target_id),
        objectId: text(accepted?.object_id),
        refused: refused ? { code: text(refused.code), message: text(refused.message) } : null,
    }
}

/**
 * `waiting` means this run is waiting for another run on the same machine - not for a person. It
 * repeats about every five seconds and has no closing event: it is over when any other event
 * arrives (the driver contract, section 3; the restore agent session, 13 Oct 2026). So a string
 * of them is shown as one, the latest, and the run is waiting only while that is the last event.
 */
export function collapseWaiting(events: AgentEvent[]): AgentEvent[] {
    return events.filter((event, i) => !(event.type === 'waiting' && events[i + 1]?.type === 'waiting'))
}
export interface Waiting { heldBy: string | null; lock: string | null; step: string | null; waitedSeconds: number | null; limitSeconds: number | null }
export function waitingNow(events: AgentEvent[]): Waiting | null {
    const last = events[events.length - 1]
    if (last?.type !== 'waiting') return null
    const text = (value: unknown) => (typeof value === 'string' && value ? value : null)
    const whole = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : null)
    return { heldBy: text(last.held_by), lock: text(last.lock), step: text(last.step), waitedSeconds: whole(last.waited_seconds), limitSeconds: whole(last.wait_limit_seconds) }
}

export function useSelfTest(sessionToken: string, deps: { bridge?: RestoreBridge; renewPass?: () => void } = {}) {
    const queryClient = useQueryClient()
    const { data: runs = [], isLoading } = useQuery({ queryKey: KEY, queryFn: async () => (await restoreRunsApi.mine()).data.data })
    const [selected, setSelected] = useState<string | null>(null)
    const [link, setLink] = useState<LinkState>('unknown')
    const [problem, setProblem] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [daemon, setDaemon] = useState<Record<string, { state: DaemonState; position?: number; exitCode?: number }>>({})
    const [events, setEvents] = useState<Record<string, AgentEvent[]>>({})

    const bridge = useMemo(() => deps.bridge ?? createRestoreBridge(), [deps.bridge])
    useEffect(() => () => { if (!deps.bridge) bridge.dispose() }, [bridge, deps.bridge])

    const token = useRef(sessionToken)
    token.current = sessionToken
    const runsRef = useRef<RestoreRun[]>(runs)
    runsRef.current = runs
    const renewPass = useRef(deps.renewPass)
    renewPass.current = deps.renewPass
    /** `unauthorized` on any request means the pass was refused: get another. */
    const refused = useCallback((error: unknown) => { if ((error as BridgeError)?.reason === 'unauthorized') renewPass.current?.() }, [])

    const putRun = useCallback((run: RestoreRun) => {
        queryClient.setQueryData<RestoreRun[]>(KEY, (list = []) => (list.some((r) => r.runId === run.runId) ? list.map((r) => (r.runId === run.runId ? run : r)) : [run, ...list]))
    }, [queryClient])

    /** Ask the daemon to replay what this page has not stored, and attach to live events. */
    const sync = useCallback(async () => {
        if (!token.current) return
        const cursors = runsRef.current.filter((r) => r.active).map((r) => ({ runId: r.runId, afterSeq: r.lastSeq }))
        try {
            await bridge.request({ type: 'restoreSync', cursors }, token.current)
            setLink('open')
            setProblem(null)
        } catch (error) {
            setProblem((error as Error).message)
            if ((error as BridgeError).code === 'no_plugin') setLink('unknown')
            refused(error)
        }
    }, [bridge, refused])

    // Events are saved one batch at a time per run, in the order they were numbered.
    const pending = useRef(new Map<string, AgentEvent[]>())
    const saving = useRef(new Set<string>())
    const expected = useRef(new Map<string, number>())
    const flush = useCallback(async (runId: string) => {
        if (saving.current.has(runId)) return
        saving.current.add(runId)
        try {
            for (;;) {
                const batch = (pending.current.get(runId) ?? []).sort((a, b) => a.seq - b.seq)
                if (!batch.length) break
                pending.current.set(runId, [])
                try {
                    putRun((await restoreRunsApi.events(runId, batch)).data.data.run)
                } catch (error) {
                    setProblem(`The events of ${runId} could not be saved: ${(error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (error as Error).message}`)
                    break
                }
            }
        } finally { saving.current.delete(runId) }
    }, [putRun])

    const onMessage = useCallback((message: BridgeMessage) => {
        if (message.kind === 'restoreLink') {
            setLink(message.state)
            // The link is back: catch up on whatever was missed while it was down.
            if (message.state === 'open') void sync()
            return
        }
        if (message.kind === 'relayError') { setProblem(message.message || 'The plug-in could not reach the daemon.'); return }
        const frame = message.frame
        if (frame.type === 'restoreEvent') {
            const { runId, event } = frame
            // Only runs Orchestrate has a record of: the daemon may be running others.
            if (!runsRef.current.some((r) => r.runId === runId)) return
            const next = expected.current.get(runId) ?? ((runsRef.current.find((r) => r.runId === runId)?.lastSeq ?? 0) + 1)
            if (event.seq > next) void sync() // a gap: something was missed
            expected.current.set(runId, Math.max(next, event.seq + 1))
            pending.current.set(runId, [...(pending.current.get(runId) ?? []), event])
            setEvents((all) => ((all[runId] ?? []).some((e) => e.seq === event.seq) || event.type === 'progress' || event.type === 'heartbeat'
                ? all : { ...all, [runId]: [...(all[runId] ?? []), event].sort((a, b) => a.seq - b.seq) }))
            void flush(runId)
        } else if (frame.type === 'auditEntry') {
            return // useAuditJournal stores these
        } else if (frame.type === 'restoreStatus') {
            const { runId, status } = frame
            setDaemon((all) => ({ ...all, [runId]: { state: status.state, position: status.position, exitCode: status.exitCode } }))
            // The daemon's own word that the process went with no result.
            if (status.state === 'lost' && runsRef.current.some((r) => r.runId === runId && r.active)) {
                restoreRunsApi.lost(runId).then((res) => putRun(res.data.data)).catch(() => {})
            }
        }
    }, [flush, putRun, sync])
    useEffect(() => bridge.subscribe(onMessage), [bridge, onMessage])

    // On load (once the runs are known and a token is there): catch up.
    const synced = useRef(false)
    useEffect(() => {
        if (synced.current || isLoading || !sessionToken) return
        synced.current = true
        void sync()
    }, [isLoading, sessionToken, sync])

    // Show the newest run until the person picks another.
    const current = runs.find((r) => r.runId === selected) ?? runs[0] ?? null
    // The events already stored for the run on screen (after coming back to the page).
    useEffect(() => {
        const runId = current?.runId
        if (!runId || events[runId]) return
        restoreRunsApi.get(runId).then((res) => setEvents((all) => (all[runId] ? all : { ...all, [runId]: res.data.data.events ?? [] }))).catch(() => {})
    }, [current?.runId, events])

    const start = useCallback(async (options: SelfTestOptions) => {
        if (!token.current) { setProblem('There is no sign-in pass yet.'); return }
        setBusy(true)
        setProblem(null)
        const runId = newRunId()
        const request = { type: 'restoreRun' as const, runId, command: COMMAND, options }
        try {
            // Orchestrate's record first: the database refuses a target or object already in use.
            putRun((await restoreRunsApi.create({ runId, command: COMMAND, request })).data.data)
            setSelected(runId)
            expected.current.set(runId, 1)
            try {
                const reply = await bridge.request(request, token.current)
                setDaemon((all) => ({ ...all, [runId]: { state: reply.state === 'queued' ? 'queued' : 'started', position: reply.position } }))
                setLink('open')
            } catch (error) {
                // No agent ran, so no event will ever close the record: close it here.
                const reason = error instanceof BridgeError ? (error.reason ?? error.code) : 'not_started'
                putRun((await restoreRunsApi.notStarted(runId, reason)).data.data)
                setProblem((error as Error).message)
                refused(error)
            }
        } catch (error) {
            setProblem((error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (error as Error).message)
        } finally { setBusy(false) }
    }, [bridge, putRun, refused])

    // An approval is signed once and used for many runs, while it covers the target, the kind of
    // action and the time (the Bridge document, section 3a). Held in memory only.
    const approval = useRef<{ approvalId: string; capability: string; notAfter: number; targets: string[]; operations: string[] } | null>(null)
    const approvalFor = useCallback(async (targetRef: string, operation: string) => {
        const held = approval.current
        if (held && held.targets.includes(targetRef) && held.operations.includes(operation) && held.notAfter - Date.now() > 10 * 60 * 1000) return held
        const res = (await restoreRunsApi.approval({ targets: [targetRef], operations: [operation as RealRun['operationClass']] })).data.data
        approval.current = { approvalId: res.approvalId, capability: res.capability, notAfter: Date.parse(res.notAfter), targets: [targetRef], operations: [operation] }
        return approval.current
    }, [])

    /**
     * A real restore (the Bridge document, section 3a), in order:
     *   1. an approval covering the target and the kind of action (a fresh device proof the first time);
     *   2. the job, signed by the back end - with the NAMES of its secrets only;
     *   3. (unless plan is false) restorePlan through the plug-in: it changes nothing and answers with the run's target and
     *      object ids, which Orchestrate's record then claims (the database refuses one in use);
     *   4. restoreStart with the typed secret values beside the job. The values go to the plug-in
     *      and nowhere else, and this function drops its reference to them as soon as they are sent.
     * Returns the run id when the run was started, else null.
     */
    const startReal = useCallback(async (run: RealRun): Promise<string | null> => {
        if (!token.current) { setProblem('There is no sign-in pass yet.'); return null }
        setBusy(true)
        setProblem(null)
        let secrets: Record<string, string> | null = run.secrets
        const runId = newRunId(new Date(), Math.random(), run.command.replace(/[^a-z0-9]/g, '').slice(0, 16) || 'run')
        try {
            const approved = await approvalFor(run.targetRef, run.operationClass)
            const signed = (await restoreRunsApi.signJob({ runId, command: run.command, operationClass: run.operationClass, targetRef: run.targetRef, options: run.options, secrets: Object.keys(run.secrets), ...(run.rateLimitBytesPerSecond ? { rateLimitBytesPerSecond: run.rateLimitBytesPerSecond } : {}) })).data.data
            const ids = run.plan === false ? { targetId: null, objectId: null, refused: null }
                : plannedIds((await bridge.request({ type: 'restorePlan', approvalId: approved.approvalId, job: signed.job, capability: approved.capability }, token.current, 60000)).data)
            if (ids.refused) { setProblem(ids.refused.message || ids.refused.code || 'The agent refused the plan.'); return null }
            // What is kept: the job's parts and the secret NAMES. Never a value.
            const request = { type: 'restoreStart', approvalId: approved.approvalId, jobId: signed.jobId, command: run.command, operationClass: run.operationClass, targetRef: run.targetRef, options: run.options, secretNames: Object.keys(run.secrets), rateLimitBytesPerSecond: run.rateLimitBytesPerSecond ?? null }
            putRun((await restoreRunsApi.create({ runId, command: run.command, request, targetId: ids.targetId, objectId: ids.objectId })).data.data)
            setSelected(runId)
            expected.current.set(runId, 1)
            try {
                // Secrets only when the job names some. The plug-in takes a restoreStart with no
                // secrets field, but refuses an empty one ("secrets must name between 1 and 32
                // values") - which is what stopped the setup screen's self-test (22 Oct 2026).
                const withSecrets = secrets && Object.keys(secrets).length > 0 ? { secrets } : {}
                const reply = await bridge.request({ type: 'restoreStart', approvalId: approved.approvalId, job: signed.job, capability: approved.capability, ...withSecrets }, token.current)
                secrets = null
                setDaemon((all) => ({ ...all, [runId]: { state: reply.state === 'queued' ? 'queued' : 'started', position: reply.position } }))
                setLink('open')
                return runId
            } catch (error) {
                const reason = error instanceof BridgeError ? (error.reason ?? error.code) : 'not_started'
                putRun((await restoreRunsApi.notStarted(runId, reason)).data.data)
                setProblem((error as Error).message)
                refused(error)
                return null
            }
        } catch (error) {
            setProblem((error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (error as Error).message)
            refused(error)
            return null
        } finally {
            secrets = null
            setBusy(false)
        }
    }, [approvalFor, bridge, putRun, refused])

    const cancel = useCallback(async (runId: string) => {
        setProblem(null)
        try { await bridge.request({ type: 'restoreStop', runId }, token.current) } catch (error) { setProblem((error as Error).message) }
    }, [bridge])

    const shown = collapseWaiting(current ? events[current.runId] ?? [] : [])
    return { bridge, runs, isLoading, current, select: setSelected, link, problem, busy, daemon: current ? daemon[current.runId] ?? null : null, events: shown, waiting: current?.active ? waitingNow(shown) : null, start, startReal, cancel, sync }
}
